import React from 'react';
import { Box } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import { neutral, primary } from '_lex/_foundations/phxColors';

export interface MarkdownRendererProps {
  content: string;
}

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content }) => {
  // Styles for the rendered markdown
  const markdownStyles = {
    '& h1': {
      fontSize: '2rem',
      fontWeight: 600,
      marginTop: 4,
      marginBottom: 2,
      color: neutral[900],
    },
    '& h2': {
      fontSize: '1.5rem',
      fontWeight: 600,
      marginTop: 3,
      marginBottom: 2,
      color: neutral[900],
    },
    '& h3': {
      fontSize: '1.25rem',
      fontWeight: 600,
      marginTop: 3,
      marginBottom: 1.5,
      color: neutral[900],
    },
    '& h4': {
      fontSize: '1.1rem',
      fontWeight: 600,
      marginTop: 2,
      marginBottom: 1,
      color: neutral[900],
    },
    '& p': {
      lineHeight: 1.3,
      marginBottom: 2,
      color: neutral[700],
    },
    '& ul, & ol': {
      marginBottom: 2,
      paddingLeft: 4,
      color: neutral[700],
    },
    '& li': {
      marginBottom: 0.5,
      lineHeight: 1.7,
    },
    '& code': {
      backgroundColor: neutral[100],
      padding: '2px 6px',
      borderRadius: '4px',
      fontSize: '0.875rem',
      fontFamily: 'Monaco, Consolas, "Courier New", monospace',
      color: neutral[900],
    },
    '& pre': {
      backgroundColor: neutral[100],
      padding: 2,
      borderRadius: 1,
      overflow: 'auto',
      marginBottom: 2,
      border: `1px solid ${neutral[200]}`,
    },
    '& pre code': {
      backgroundColor: 'transparent',
      padding: 0,
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    '& blockquote': {
      borderLeft: `4px solid ${primary[500]}`,
      paddingLeft: 2,
      marginLeft: 0,
      marginBottom: 2,
      color: neutral[600],
      fontStyle: 'italic',
    },
    '& table': {
      width: '100%',
      marginBottom: 2,
      borderCollapse: 'collapse',
      border: `1px solid ${neutral[200]}`,
    },
    '& th': {
      backgroundColor: neutral[100],
      padding: 2,
      textAlign: 'left',
      fontWeight: 600,
      borderBottom: `2px solid ${neutral[300]}`,
      borderRight: `1px solid ${neutral[200]}`,
      color: neutral[900],
    },
    '& td': {
      padding: 2,
      borderBottom: `1px solid ${neutral[200]}`,
      borderRight: `1px solid ${neutral[200]}`,
      color: neutral[700],
    },
    '& a': {
      color: primary[600],
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    '& hr': {
      border: 'none',
      borderTop: `1px solid ${neutral[200]}`,
      marginTop: 3,
      marginBottom: 3,
    },
    '& strong': {
      fontWeight: 600,
      color: neutral[900],
    },
    '& em': {
      fontStyle: 'italic',
    },
  };

  return (
    <Box
      className='markdown-content'
      sx={markdownStyles}
    >
      <ReactMarkdown
        components={{
          a: ({ node, href, children, ...props }) => (
            <a
              href={href}
              target={href?.startsWith('http') ? '_blank' : undefined}
              rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
              {...props}
            >
              {children}
            </a>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </Box>
  );
};

export default MarkdownRenderer;
